import React, { useState } from "react";

const Account_Deletion_Requests = () => {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const normalizedEmail = email.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!confirmed) {
      setError("Please confirm your account deletion request.");
      return;
    }

    setStatus("loading");

    try {
      //   const apiBaseUrl = (import.meta.VITE_API_BASE_URL || "").replace(
      //     /\/$/,
      //     "",
      //   );
      const apiBaseUrl = "https://dashdynamic.in";

      const response = await fetch(
        `${apiBaseUrl}/api/account-deletion-requests`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: normalizedEmail,
            reason: reason.trim() || undefined,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      //   if (!response.ok) {
      //     throw new Error(
      //       data.message ||
      //         "We couldn't submit your request. Please try again later.",
      //     );
      //   }

      setStatus("success");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again later.");
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen bg-white px-4 py-14 sm:px-6 sm:py-28">
      <div className="mx-auto max-w-6xl">
        {/* Page heading */}
        <div className="mb-12 max-w-3xl">
          <h1 className="text-3xl font-normal tracking-tight text-red-800 sm:text-5xl">
            Delete your account
          </h1>

          <div className="mt-5 h-1 w-16 bg-red-800" />

          <p className="mt-6 text-base leading-7 text-gray-700 sm:text-lg">
            We're sorry to see you go. If you wish to leave Dash Dynamic, you
            can submit a request to delete your account and associated personal
            data using the form below.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_0.85fr] lg:gap-12">
          {/* Deletion information */}
          <section className="space-y-5">
            <div className="border-l-4 border-red-800 bg-gray-50 p-6 sm:p-8">
              <h2 className="text-xl font-medium text-gray-900">
                What you should know
              </h2>

              <p className="mt-4 leading-7 text-gray-700">
                Once your request is received, we will verify your account
                ownership and process your deletion request.
              </p>

              <div className="mt-7 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-800">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      className="h-6 w-6"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" strokeLinecap="round" />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">
                      Verification and processing
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      We may contact you to verify that you own the account
                      before processing your request.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-800">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      className="h-6 w-6"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 7h16M10 11v6m4-6v6M6 7l1 14h10l1-14M9 7V4h6v3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">
                      Account and personal data
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Your account and associated personal data will be deleted
                      in accordance with our deletion policy, subject to
                      applicable legal retention requirements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-800">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      className="h-6 w-6"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 3 4.5 6v5.5c0 4.5 3.2 7.5 7.5 9.5 4.3-2 7.5-5 7.5-9.5V6L12 3Z"
                        strokeLinejoin="round"
                      />
                      <path
                        d="m9 12 2 2 4-4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">
                      Permanent deletion
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Deletion may be permanent, and you may lose access to your
                      account and associated services.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm leading-6 text-gray-500">
              Need assistance? Contact our support team if you have questions
              about your request or the deletion process.
            </p>
          </section>

          {/* Request form */}
          <section className="h-fit border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
            {status === "success" ? (
              <div
                className="py-8 text-center"
                role="status"
                aria-live="polite"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-700">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-8 w-8"
                    aria-hidden="true"
                  >
                    <path
                      d="m5 12 4 4L19 6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <h2 className="mt-5 text-2xl font-medium text-gray-900">
                  Request submitted
                </h2>

                <p className="mt-3 leading-7 text-gray-600">
                  Your request has been received. We may contact you at your
                  registered email address to verify your identity and complete
                  the process.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setEmail("");
                    setReason("");
                    setConfirmed(false);
                    setError("");
                    setStatus("idle");
                  }}
                  className="mt-7 border border-red-800 px-6 py-3 text-sm font-medium text-red-800 transition hover:bg-red-800 hover:text-white"
                >
                  Back to form
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-medium text-gray-900">
                  Submit a request
                </h2>

                <p className="mt-3 text-sm leading-6 text-gray-600">
                  Enter the email address associated with your account.
                </p>

                <form onSubmit={handleSubmit} className="mt-7 space-y-6">
                  <div>
                    <label
                      htmlFor="deletion-email"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Registered email address
                      <span className="ml-1 text-red-700">*</span>
                    </label>

                    <input
                      id="deletion-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                      maxLength={254}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      className="w-full border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-red-800 focus:ring-1 focus:ring-red-800"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="deletion-reason"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Reason for deletion{" "}
                      <span className="font-normal text-gray-500">
                        (Optional)
                      </span>
                    </label>

                    <textarea
                      id="deletion-reason"
                      name="reason"
                      rows={4}
                      maxLength={1000}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Tell us why you want to delete your account..."
                      className="w-full resize-y border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-red-800 focus:ring-1 focus:ring-red-800"
                    />

                    <p className="mt-1 text-right text-xs text-gray-400">
                      {reason.length}/1000
                    </p>
                  </div>

                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      required
                      className="mt-1 h-4 w-4 shrink-0 accent-red-800"
                    />

                    <span className="text-sm leading-6 text-gray-600">
                      I confirm that I want to request deletion of my Dash
                      Dynamic account and understand that this action may be
                      permanent.
                    </span>
                  </label>

                  {error && (
                    <div
                      role="alert"
                      className="border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="flex w-full items-center justify-center gap-2 bg-red-800 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-red-800 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            stroke="currentColor"
                            strokeOpacity=".25"
                            strokeWidth="3"
                          />
                          <path
                            d="M21 12a9 9 0 0 0-9-9"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit deletion request"
                    )}
                  </button>

                  <p className="text-xs leading-5 text-gray-500">
                    The information you provide will be used to process your
                    request. Please review our{" "}
                    <a
                      href="/app-privacy"
                      className="font-medium text-red-800 underline underline-offset-2 hover:text-red-950"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default Account_Deletion_Requests;
